import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil } from 'lucide-react';

interface CompactInputProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'number';
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
}

export const CompactInput: React.FC<CompactInputProps> = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  min,
  max,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(String(value));
  }, [value]);

  const handleSave = () => {
    onChange(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(String(value));
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Display value - mask passwords
  const displayValue = type === 'password' && value
    ? '••••••••'
    : value || null;

  const hasValue = value !== '' && value !== undefined && value !== null;

  if (isEditing) {
    return (
      <div className="flex items-center gap-3 py-1.5 px-1">
        {/* Status indicator */}
        <div className={`w-2 h-2 rounded-full shrink-0 ${hasValue ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
        {/* Label */}
        <span className="w-24 text-sm font-medium shrink-0">{label}</span>
        {/* Input */}
        <Input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          min={min}
          max={max}
          className="h-8 flex-1"
        />
        {/* Save/Cancel buttons */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleSave}
          className="h-8 w-8 p-0"
        >
          <Check className="h-4 w-4 text-green-600" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2 px-1 hover:bg-muted/50 rounded-md">
      {/* Status indicator */}
      <div className={`w-2 h-2 rounded-full shrink-0 ${hasValue ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
      {/* Label */}
      <span className="w-24 text-sm font-medium shrink-0">{label}</span>
      {/* Value */}
      <span
        className={`flex-1 text-sm truncate ${
          hasValue ? 'text-foreground font-mono' : 'text-muted-foreground italic'
        }`}
      >
        {displayValue || 'Not set'}
      </span>
      {/* Edit button */}
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="h-4 w-4 mr-1" />
          Edit
        </Button>
      )}
    </div>
  );
};

export default CompactInput;
